import { useEffect, useMemo, useState } from 'react';
import { iptv } from '../api';
import { ChannelName } from './LiveBits';

type Cat = { id: string; name: string; count: number; hidden: boolean };
type Ch = { streamId: number; name: string; categoryId: string; hidden: boolean };

/**
 * Bouquets (categorieën) en losse zenders aan/uit zetten voor één IPTV-account.
 * Verborgen zenders verdwijnen uit de gids, de sportlijst en het zappen, voor alle profielen.
 */
export function ChannelManager({ accountId, onSaved }: { accountId: string; onSaved: () => void }) {
  const [cats, setCats] = useState<Cat[] | null>(null);
  const [channels, setChannels] = useState<Ch[]>([]);
  const [hiddenCats, setHiddenCats] = useState<Set<string>>(new Set());
  const [hiddenStreams, setHiddenStreams] = useState<Set<number>>(new Set());
  const [open, setOpen] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    iptv.accountChannels(accountId)
      .then((r) => {
        setCats(r.categories);
        setChannels(r.channels);
        setHiddenCats(new Set(r.categories.filter((c) => c.hidden).map((c) => c.id)));
        setHiddenStreams(new Set(r.channels.filter((c) => c.hidden).map((c) => c.streamId)));
      })
      .catch(() => setError('Zenders laden mislukt.'));
  }, [accountId]);

  const q = query.trim().toLowerCase();
  const visibleCats = useMemo(() => {
    if (!cats) return [];
    if (!q) return cats;
    const catHasMatch = new Set(channels.filter((c) => c.name.toLowerCase().includes(q)).map((c) => c.categoryId));
    return cats.filter((c) => c.name.toLowerCase().includes(q) || catHasMatch.has(c.id));
  }, [cats, channels, q]);

  const toggleCat = (id: string) => {
    setHiddenCats((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
    setDirty(true);
  };
  const toggleStream = (id: number) => {
    setHiddenStreams((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
    setDirty(true);
  };
  const setAllVisible = (visible: boolean) => {
    const ids = visibleCats.map((c) => c.id);
    setHiddenCats((s) => {
      const n = new Set(s);
      for (const id of ids) {
        if (visible) n.delete(id);
        else n.add(id);
      }
      return n;
    });
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await iptv.updateAccount(accountId, { hiddenCategories: [...hiddenCats], hiddenStreams: [...hiddenStreams] });
      setDirty(false);
      onSaved();
    } catch {
      setError('Opslaan mislukt.');
    } finally {
      setBusy(false);
    }
  };

  if (error && !cats) return <p className="error" role="alert">{error}</p>;
  if (!cats) return <p className="subtle">Bouquets en zenders laden…</p>;

  const shownCount = cats.filter((c) => !hiddenCats.has(c.id)).length;

  return (
    <div className="chman">
      <p className="subtle">
        {shownCount} van {cats.length} bouquets zichtbaar. Zet bouquets uit die je nooit bekijkt; open een bouquet om losse zenders uit te zetten.
      </p>
      <div className="filterbar" style={{ padding: 0 }}>
        <label htmlFor={`chq-${accountId}`} className="visually-hidden">Zoek bouquet of zender</label>
        <input id={`chq-${accountId}`} className="text-input text-input-sm" type="search" placeholder="Zoek bouquet of zender…" value={query} onChange={(e) => setQuery(e.target.value)} />
        <button className="chip" onClick={() => setAllVisible(true)}>{q ? 'Gevonden aan' : 'Alles aan'}</button>
        <button className="chip" onClick={() => setAllVisible(false)}>{q ? 'Gevonden uit' : 'Alles uit'}</button>
      </div>

      <ul className="chman-list">
        {visibleCats.map((c) => {
          const on = !hiddenCats.has(c.id);
          const catChannels = open === c.id ? channels.filter((ch) => ch.categoryId === c.id && (!q || ch.name.toLowerCase().includes(q) || c.name.toLowerCase().includes(q))) : [];
          const hiddenInCat = channels.filter((ch) => ch.categoryId === c.id && hiddenStreams.has(ch.streamId)).length;
          return (
            <li key={c.id} className={`chman-cat ${on ? '' : 'is-off'}`}>
              <div className="chman-row">
                <label className="toggle">
                  <input type="checkbox" checked={on} onChange={() => toggleCat(c.id)} />
                  <span>{c.name}</span>
                </label>
                <span className="subtle">{c.count} zenders{hiddenInCat ? ` · ${hiddenInCat} uit` : ''}</span>
                {on && (
                  <button className="chip" aria-expanded={open === c.id} onClick={() => setOpen(open === c.id ? null : c.id)}>
                    {open === c.id ? 'Sluiten' : 'Zenders'}
                  </button>
                )}
              </div>
              {open === c.id && on && (
                <ul className="chman-channels">
                  {catChannels.map((ch) => (
                    <li key={ch.streamId}>
                      <label className="toggle">
                        <input type="checkbox" checked={!hiddenStreams.has(ch.streamId)} onChange={() => toggleStream(ch.streamId)} />
                        <ChannelName name={ch.name} />
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>

      {error && <p className="error" role="alert">{error}</p>}
      <div className="form-actions chman-actions">
        <button className="btn btn-primary" onClick={() => void save()} disabled={!dirty || busy}>{busy ? 'Opslaan…' : dirty ? 'Wijzigingen opslaan' : 'Opgeslagen'}</button>
      </div>
    </div>
  );
}
